import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useCountdown } from '@/features/countdown/hooks/useCountdown';
import { useCountdownQuery } from '@/features/countdown/hooks/useCountdownQuery';

/**
 * The HUD title line, flipping between the dashboard name and the event
 * countdown.
 *
 * One slot carrying two things, rather than two slots: the leadership
 * dashboards below are fitted to a single viewport, so the header cannot grow,
 * and the HUD ornament under this line is too shallow to hold readable digits.
 * Flipping trades a little patience for no layout cost at all.
 *
 * The countdown face is bare `dd:hh:mm:ss` with the countdown's own title in
 * small caps beneath it. No "T-minus" prefix: the caption already says what is
 * being counted down to, and saying it twice in one 28px line crowded out the
 * digits that are the point.
 *
 * Rotation stops entirely under `prefers-reduced-motion`, leaving the dashboard
 * name in place — the same bargain `ExecutiveFormatDashboard` strikes with its
 * auto-rotating performer panel, and the reason it is the title that stays: it
 * is the one of the two that names where you are.
 *
 * Plain tabular-nums for the digits. The DSEG7 seven-segment webfont was tried
 * and rendered tofu in production; do not reintroduce one.
 */

const FLIP_INTERVAL_MS = 5_000;

const pad = (n) => String(n).padStart(2, '0');

/**
 * Both faces put their main line in the same 28px band at the top of the card,
 * so the flip swaps content without the text appearing to move.
 *
 * Line height rather than flex centring: an ellipsis needs a block box, and a
 * flexed face would clip a long dashboard name mid-glyph instead of truncating
 * it.
 */
const FACE_CLASS =
    'absolute inset-x-0 top-0 block truncate text-center font-header text-lg font-bold uppercase leading-7 tracking-[0.25em] text-primary-600 sm:text-xl dark:text-primary-400';

/**
 * The countdown's own title, hung under the digits at `top-9` so it lands
 * inside the ornament's trapezoid rather than across its top edge — the gap
 * directly beneath the digits is only ~5px, too tight to sit in cleanly.
 */
const CAPTION_CLASS =
    'block truncate text-center text-[9px] font-bold uppercase leading-[0.5rem] tracking-[0.2em] text-primary-600/70 dark:text-primary-400/70';

const TITLE_GLOW = {
    textShadow: '0 0 14px color-mix(in srgb, var(--primary-500) 50%, transparent)',
};

const FACE_STYLE = {
    ...TITLE_GLOW,
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
};

export function HeaderTitleFlip({ title, showCountdown = false }) {
    const { countdown } = useCountdownQuery();
    const { days, hours, minutes, seconds, elapsed, valid } = useCountdown(
        countdown?.targetAt ?? null,
    );
    const [flipped, setFlipped] = useState(false);

    // No countdown configured is a real state, not zeros — with nothing to flip
    // to, the line simply stays the dashboard name.
    const canFlip = showCountdown && valid;

    useEffect(() => {
        // No reset on the way out. With nothing to flip to the static title
        // renders instead, and under reduced motion the card never leaves its
        // initial face — so neither path has a stored face worth clearing.
        if (!canFlip) return undefined;
        if (typeof window === 'undefined') return undefined;

        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (prefersReducedMotion) return undefined;

        const id = window.setInterval(() => setFlipped((f) => !f), FLIP_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [canFlip]);

    if (!canFlip) {
        return (
            <span
                className="mt-2 inline-block max-w-full font-header text-lg font-bold uppercase tracking-[0.25em] text-primary-600 sm:text-xl dark:text-primary-400"
                style={TITLE_GLOW}
            >
                {title}
            </span>
        );
    }

    // Bare dd:hh:mm:ss — what is being counted down to is said by the caption
    // beneath, not by a prefix on the digits. `pad` only ever pads, so a day
    // count past 99 widens the field rather than losing its leading digit.
    const digits = `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

    // Once the moment has passed there is nothing to count, and zeros would
    // read as "now". The caption's job moves up into the main line.
    const eventLabel = countdown?.title || 'Event underway';
    const caption = elapsed ? null : countdown?.title;

    return (
        // h-12 with -mb-5 on purpose. The HUD column already fills the header
        // to the pixel, so the card gives back exactly what it takes and its
        // flow stays the original 28px — grow it and the ornament shifts down
        // for every role that shares this header. The caption then hangs
        // outside the flow, landing inside the trapezoid, which is the one
        // pocket of empty space the HUD has.
        <div className="mt-2 h-12 -mb-5 w-full" style={{ perspective: '600px' }}>
            <div
                className="relative h-full w-full transition-transform duration-700 ease-in-out [transform-style:preserve-3d]"
                style={{ transform: flipped ? 'rotateX(180deg)' : 'rotateX(0deg)' }}
            >
                <span className={FACE_CLASS} style={FACE_STYLE}>
                    {title}
                </span>
                {/*
                  * Hidden from assistive tech on purpose. Both faces live in
                  * the DOM at once, so a screen reader would otherwise read the
                  * dashboard name and a frozen timestamp back to back. The
                  * countdown is ambient here; the Operational Dashboard carries
                  * the one that is meant to be read.
                  */}
                <span
                    className="absolute inset-0 block"
                    style={{ ...FACE_STYLE, transform: 'rotateX(180deg)' }}
                    aria-hidden="true"
                >
                    <span
                        className={cn(
                            FACE_CLASS,
                            !elapsed && 'font-mono tabular-nums tracking-[0.18em]',
                        )}
                    >
                        {elapsed ? eventLabel : digits}
                    </span>
                    {caption ? (
                        <span className={cn(CAPTION_CLASS, 'absolute inset-x-0 top-9')}>
                            {caption}
                        </span>
                    ) : null}
                </span>
            </div>
        </div>
    );
}

export default HeaderTitleFlip;
